import React, { useState, useRef, useEffect } from 'react';
import { cloneDeep, set } from 'lodash';
import { Auth } from 'aws-amplify';
import * as Sentry from "@sentry/react";
import ClientUploads from './components/ClientUploads';
import PrimaryCharts from './components/PrimaryCharts';
import { DeviceInfoTable } from './components/DeviceInfoTable';
import classes from './App.module.css';
import 'antd/dist/reset.css';
import { SettingsButtons } from './components/SettingsButtons';
import { useAtom } from 'jotai';
import Lottie from "lottie-react";
import loadingAnimation from './images/loading.json';
import lineSwirlLoadAnimation from './images/lineSwirlLoad.json';
import {
  minLaserPowerState,
  maxLaserPowerState,
  minIntegrationTimeState,
  maxIntegrationTimeState,
  defaultCenter,
  centerState
} from './stateManagement/controlButtonState';
import {
  mapState,
  currentUserState,
  userNameState,
  deviceInfoState,
  isAdminState,
  traceMapState,
  allSubstancesDataState,
  isNewRecordCreatedState,
  imageFilenameState,
  isInspectionModalOpenState,
  isOperatorModalOpenState,
  mapReprocessTriggerState,
  wifiCharacteristicState,
  bluetoothServiceState,
  imageCharacteristicState,
  userGroupsState,
  riskAwarenessShouldUpdateState,
  isAllPredictionsPanelOpenState,
  currentSerialState,
  loadingMapState,
  isValidatedSpectraTableOpenState,
  currentMapIDState,
  isResultOptionsPanelOpenState,
  userTraceDetectionPreferencesState,
  currentTraceDetectionPreferencesState,
  traceOptionsReadyState,
  allScanDataState
} from './stateManagement/commonState';
import {
  scanningState,
  scanHeightState,
  scanWidthState,
  pointsPerSideState,
  numLevelsState,
  numPointsState,
  numPointsAcquiredState,
  singlePointMultiplierState,
  currentPathState,
  calibrationInfoState,
  defaultCalibrationInfo,
  scanTypeState,
  darkReferenceSpectrumState,
  calibrateState,
  scanPhaseState,
} from './stateManagement/processState';
import {
  Row,
  Col,
  message,
  Modal,
  Layout,
  Radio,
  notification,
  Progress,
  Button,
  Tooltip,
  Alert
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { substanceArray, substanceMap, substanceThresholds } from './utils/substancesAndColors';
import {
  connectButtonHandler,
  subscribeToCharacteristic,
  disconnectBluetoothDevice,
  writeToDevice,
  readEEPROM
} from './utils/bluetoothLogic';
import {
  calibrateRamanIntensity,
  removeBadPixels,
  interpolate,
  chunkArrayIntoN,
  arrayBufferToBufferCycle,
  createMasterDarkReference
} from './utils/helpers';
import {
  uploadSpectra,
  fetchSpectrum,
  fetchArchivedSpectra,
  fetchSubstances,
  fetchDeviceInfo,
  updateDeviceFirmwareVersionInDB,
  updateDeviceNetworkStatusInDB,
  updateDeviceCameraStatusInDB,
  uploadResults,
  getUserGroups,
  saveDarkReferenceSpectrum,
  fetchDarkReferenceSpectrum,
  getUserTraceDetectionPreferences,
  updateUserTraceDetectionPreferences,
  LOCAL_SCAN_STORAGE
} from './utils/dbHelpers';
import { calculateCenterZ } from './utils/calibrationHelpers';
import { getExpandingSquareSearchPoints } from './utils/getExpandingSquareSearch';
import logoImage from './images/ScatrLogo.svg';
import { parseEEPROM } from './utils/parseEEPROM';
import { getWavenumbers } from './utils/wavenumbers';
import ActionButtons from './components/ActionButtons';
import WifiConfigModal from './components/WifiConfigModal';
import TenantManagerModal from './components/TenantManagerModal.js';
import { smooth } from './utils/modifiedSincSmoother.js'
import { AdditionalSettings } from './components/AdditionalSettings';
import ScanWidthToggle from './components/ScanWidthToggle';
import LaserPowerToggle from './components/LaserPowerToggle';
import { model1State, model2State } from './stateManagement/mlState';
import MassSpectrometryContainer from './components/MassSpectrometry/MassSpectrometryContainer';
import SubstanceInfoModal from './components/SubstanceInfoModal';
import SubstanceManagement from './components/SubstanceManagement';
import InspectionModal from './components/InspectionModal';
import RiskAwareness from './components/RiskAwareness';
import OperatorModal from './components/OperatorModal';
import { computeResults } from './utils/results.js';
import BluetoothImage from './components/BluetoothImage.js';
import AllPredictionsPanel from './components/AllPredictionsPanel.js';
import ValidatedSpectraTable from './components/ValidatedSpectraTable.js';
import ResultOptionsPanel from './components/ResultOptionsPanel.js';

import {
  loadModels,
  getReconstructionError,
  getMLPredictionArray,
  getFentanylPrediction,
  getXylazinePrediction,
  getBenzodiazepinePrediction,
  getHydromorphonePrediction
} from './predict';

import { ASPLS } from './utils/baseline_aspls.js';
import { classifySpectrum, detectTraceFentanyl, detectTraceHeroin, shouldShowTraceHeroin, detectTraceHydromorphone, detectTraceBenzodiazepine, detectTraceXylazine, detectTraceMedetomidine } from './utils/detect.js';
import { processReferenceScanDataPoint } from './utils/referenceScanLogic';
import { calculateIncludeValue } from './components/ResultOptionsPanel.js';
import { benzodiazepineLabels } from './utils/detect.js';
import {
  defaultTraceDetectionPreferences,
  mergeTraceDetectionPreferences
} from './utils/traceDetectionPreferences.js';

import { dswSNR } from './utils/dswSNR';

require('nerdamer/Solve');

const scanSettings = { 'quick': 7, 'standard': 13, 'detailed': 18, 'reference': 3 };

const standardWavenumbers = [...Array(2324 - 225 + 1)].map((_, i) => 225 + i);

const { Footer, Content } = Layout;

const key = 'updatable';

const tf = require('@tensorflow/tfjs');

let device;
let characteristic;

let bluetoothStream = [];

let worker;
let tensorflowWorker = [];
let numTensorflowWorkers = 1;

const numDarkSpectraToAcquire = 60;

function App({ signOut }) {
  const [isReady, setIsReady] = useState(false);
  const [primaryWorkerInitialized, setPrimaryWorkerInitialized] = useState(false);
  const [tensorflowWorkersInitialized, setTensorflowWorkersInitialized] = useState(false);
  const [clientUploadsWorkerInitialized, setClientUploadsWorkerInitialized] = useState(false);
  const numTensorflowWorkersInitialized = useRef(0);
  const [currentUser, setCurrentUser] = useAtom(currentUserState);
  const [username] = useAtom(userNameState);
  const [userGroups, setUserGroups] = useAtom(userGroupsState);
  const [isAdmin, setIsAdmin] = useAtom(isAdminState);
  const [isInspectionModalOpen] = useAtom(isInspectionModalOpenState);
  const [, setIsOperatorModalOpen] = useAtom(isOperatorModalOpenState);
  const [isAllPredictionsPanelOpen] = useAtom(isAllPredictionsPanelOpenState);
  const [isValidatedSpectraTableOpen] = useAtom(isValidatedSpectraTableOpenState);
  const [isResultOptionsPanelOpen, setIsResultOptionsPanelOpen] = useAtom(isResultOptionsPanelOpenState);

  const [settings, setSettings] = useState(false);
  const [showDeviceInfo, setShowDeviceInfo] = useState(false);
  const [map, setMap] = useAtom(mapState);
  const mapRef = useRef([]);
  const [traceMap, setTraceMap] = useAtom(traceMapState);
  const traceMapRef = useRef([]);
  const footerRef = React.useRef(null);

  const [scanType, setScanType] = useAtom(scanTypeState);
  const scanTypeRef = useRef(null);
  const [scanWidth, setScanWidth] = useAtom(scanWidthState);
  const scanWidthRef = useRef(null);
  const [scanHeight, setScanHeight] = useAtom(scanHeightState);
  const scanHeightRef = useRef(null);
  const [pointsPerSide, setPointsPerSide] = useAtom(pointsPerSideState);
  const pointsPerSideRef = useRef(null);
  const [numLevels, setNumLevels] = useAtom(numLevelsState);
  const numLevelsRef = useRef(null);
  const [numPoints, setNumPoints] = useAtom(numPointsState);
  const numPointsRef = useRef(0);
  const [scanProgress, setScanProgress] = useState(0);
  const totalNumSpectraAcquiredRef = useRef(0);
  const targetNumSpectraRef = useRef(0);
  const numSpectrumReqSentRef = useRef(0);
  const numSpectraAcquiredRef = useRef(0);
  const [singlePointMultiplier, setSinglePointMultiplier] = useAtom(singlePointMultiplierState);
  const singlePointMultiplierRef = useRef(null);

  const [minLaserPower, setMinLaserPower] = useAtom(minLaserPowerState);
  const [maxLaserPower, setMaxLaserPower] = useAtom(maxLaserPowerState);
  const maxLaserPowerRef = useRef(null);
  const [minIntegrationTime, setMinIntegrationTime] = useAtom(minIntegrationTimeState);
  const [maxIntegrationTime, setMaxIntegrationTime] = useAtom(maxIntegrationTimeState);
  const maxIntegrationTimeRef = useRef(maxIntegrationTime);
  const [center, setCenter] = useAtom(centerState);

  const [darkReferenceSpectrum, setDarkReferenceSpectrum] = useAtom(darkReferenceSpectrumState);
  const darkReferenceSpectrumRef = useRef(null);
  const darkReferenceSpectraRef = useRef([]);

  const [shouldReprocessTraceMap, setShouldReprocessTraceMap] = useState(false);

  const [scanning, setScanning] = useAtom(scanningState);
  const scanningRef = React.useRef(false);
  const [scanPhase, setScanPhase] = useAtom(scanPhaseState);
  const scanPhaseRef = useRef(null);
  const [loadingMap, setLoadingMap] = useAtom(loadingMapState);
  const [calibrate, setCalibrate] = useAtom(calibrateState);
  const calibrateRef = useRef(calibrate);
  const [isNewRecordCreated, setIsNewRecordCreated] = useAtom(isNewRecordCreatedState);
  const [imageFilename] = useAtom(imageFilenameState);

  const [currentDataPoint, setCurrentDataPoint] = useState({});
  const [currentSerial, setCurrentSerial] = useAtom(currentSerialState);
  const [currentMapID, setCurrentMapID] = useAtom(currentMapIDState);
  const currentMapIDRef = useRef(null);
  const [currentPath] = useAtom(currentPathState);
  const pathHashMapRef = useRef(new Map());

  const [deviceInfo, setDeviceInfo] = useAtom(deviceInfoState);
  const connectedDeviceInfoRef = useRef(null);
  const deviceInfoRef = useRef(null);
  const [deviceWavenumbers, setDeviceWavenumbers] = useState([]);
  const deviceWavenumbersRef = React.useRef([]);

  const workerMessageQueueRef = useRef([]);
  const workerMessageProcessingRef = useRef(false);
  const tensorflowWorkerMessageQueue = useRef([]);

  const dataPointProcessingQueueRef = useRef([]);
  const dataPointProcessingInProgressRef = useRef(false);

  const warmupDelayTimeoutID = useRef(null);

  const [, setModel_1] = useAtom(model1State);
  const [, setModel_2] = useAtom(model2State);

  const consecutiveSaturatedCountRef = useRef(0);

  const [wifiConfigModalOpen, setWifiConfigModalOpen] = useState(false);
  const [isTenantsModalOpen, setIsTenantsModalOpen] = useState(false);
  const [bluetoothState, setBluetoothState] = useState('Disconnected');
  const [, setAllSubstancesData] = useAtom(allSubstancesDataState);

  const [mapReprocessTrigger, setMapReprocessTrigger] = useAtom(mapReprocessTriggerState);
  const [notificationAPI, notificationContextHolder] = notification.useNotification();
  const [mapReprocessProgress, setMapReprocessProgress] = useState(0);

  const [, setWifiCharacteristic] = useAtom(wifiCharacteristicState);
  const [, setBluetoothService] = useAtom(bluetoothServiceState);
  const [imageCharacteristic] = useAtom(imageCharacteristicState);

  const [, setRiskAwarenessShouldUpdate] = useAtom(riskAwarenessShouldUpdateState);

  const [laserPowerSetting, setLaserPowerSetting] = useState('Full');
  const [lastKnownLaserPowerSetting, setLastKnownLaserPowerSetting] = useState('Full');

  const referenceScanPhaseRef = useRef('calibrateY');

  const [userTraceDetectionPreferences, setUserTraceDetectionPreferences] = useAtom(userTraceDetectionPreferencesState);
  const [currentTraceDetectionPreferences, setCurrentTraceDetectionPreferences] = useAtom(currentTraceDetectionPreferencesState);
  const currentTraceDetectionPreferencesRef = useRef(currentTraceDetectionPreferences);
  const [, setTraceOptionsReady] = useAtom(traceOptionsReadyState);
  const [allScanData] = useAtom(allScanDataState);

  useEffect(() => {
    if (laserPowerSetting) { setLastKnownLaserPowerSetting(laserPowerSetting); }
  }, [laserPowerSetting]);

  const updatePathHashMap = (path, integrationTimes, createNew = false) => {
    const prevHashMap = pathHashMapRef.current;
    if (path.length !== integrationTimes.length) { console.error('Path and integration times length mismatch'); return; }
    if (createNew) { pathHashMapRef.current = new Map(); }
    const totalIndices = Array.from(pathHashMapRef.current.values()).reduce((sum, { indices }) => sum + indices.length, 0);
    for (let i = 0; i < path.length; i++) {
      const index = totalIndices + i;
      const key = path[i].join(',');
      if (pathHashMapRef.current.has(key)) {
        pathHashMapRef.current.get(key).indices.push(index);
        pathHashMapRef.current.get(key).integrationTimes.push(integrationTimes[i]);
      }
      if (!pathHashMapRef.current.has(key)) {
        pathHashMapRef.current.set(key, { indices: [index], integrationTimes: [integrationTimes[i]], counter: 0 });
      }
    }
    updateTargetNumSpectra();
  }

  const updateTargetNumSpectra = () => {
    const integrationTimes = [...pathHashMapRef.current.values()].flatMap(v => Array.isArray(v.integrationTimes) ? v.integrationTimes : []);
    const num500msIntegrationTimes = integrationTimes.reduce((acc, time) => (acc + (time / 500)), 0);
    targetNumSpectraRef.current = scanTypeRef.current === 'reference' ? 287 : num500msIntegrationTimes;
  }

  const createMapHashMap = (map) => {
    return map.reduce((mapHash, item) => {
      const { index, x, y, z, integrationTime } = item;
      const key = `${x},${y},${z}`;
      if (!mapHash.has(key)) {
        mapHash.set(key, { indices: [], integrationTimes: [], counter: 0 });
      }
      mapHash.get(key).indices.push(index);
      mapHash.get(key).integrationTimes.push(integrationTime);
      return mapHash;
    }, new Map());
  }

  const processEpoch = () => {

    numSpectraAcquiredRef.current = 0;
    numSpectrumReqSentRef.current = 0;

    if (totalNumSpectraAcquiredRef.current === targetNumSpectraRef.current) { setScanning(false); return; }

    const mapHash = createMapHashMap(mapRef.current);

    const pointsToRequest = [];
    for (const [key, pathValue] of pathHashMapRef.current) {
      const mapIntegrationTimes = mapHash.get(key)?.integrationTimes ?? null;
      const done = mapIntegrationTimes && pathValue.integrationTimes.length === mapIntegrationTimes.length && pathValue.integrationTimes.every((v, i) => v === mapIntegrationTimes[i])
      if (!done) { pointsToRequest.push(key.split(',')); }
    }

    if (pointsToRequest.length === 0) { setScanning(false); return; }

    const nextPath = pointsToRequest.map((point, i) => [Math.round(point[0] * 1e6), Math.round(point[1] * 1e6), Math.round(point[2] * 1e6)]);
    let commands = [
      ['laserON'],
      ['sendCoordinates', nextPath],
      ['scanCoordinates'],
      ['laserOFF'],
      ['executeCommands']
    ];
    numSpectrumReqSentRef.current = nextPath.length;
    writeToDevice(characteristic, commands);
  }

  const applyCalibrationAndProceed = () => {

    const newCenter = { x: connectedDeviceInfoRef.current.center_calibration.x, y: connectedDeviceInfoRef.current.center_calibration.y, z: calculateCenterZ(mapRef.current) };
    setCenter(newCenter);
    setScanPhase('Scanning');

    const points = getExpandingSquareSearchPoints(newCenter, scanWidthRef.current, scanHeightRef.current, pointsPerSideRef.current, numLevelsRef.current, singlePointMultiplierRef.current);
    const path = points.map((point, i) => [Math.round(point[0] * 1e6), Math.round(point[1] * 1e6), Math.round(point[2] * 1e6)]);

    totalNumSpectraAcquiredRef.current = 0;
    numSpectrumReqSentRef.current = path.length;
    updatePathHashMap(points, Array(points.length).fill(maxIntegrationTimeRef.current));

    const commands = [
      ['setIntegrationTime', 500],
      ['setLaserPower', maxLaserPowerRef.current],
      ['sendCoordinates', path],
      ['scanCoordinates'],
      ['executeCommands']
    ];
    writeToDevice(characteristic, commands);
  }

  const processCalibrationEpoch = () => {

    numSpectraAcquiredRef.current = 0;
    numSpectrumReqSentRef.current = 0;

    const mapHash = createMapHashMap(mapRef.current);

    if (mapRef.current.map(e => Math.max(...e.processedSpectrum)).filter(maxIntensity => maxIntensity > 200).length >= 5) {
      pathHashMapRef.current = mapHash;
      applyCalibrationAndProceed();
      return;
    }

    const pointsToRequest = [];
    for (const [key, pathValue] of pathHashMapRef.current) {
      const mapIntegrationTimes = mapHash.get(key)?.integrationTimes ?? null;
      const done = mapIntegrationTimes && pathValue.integrationTimes.length === mapIntegrationTimes.length && pathValue.integrationTimes.every((v, i) => v === mapIntegrationTimes[i])
      if (!done) { pointsToRequest.push(key.split(',')); }
    }

    if (pointsToRequest.length === 0) { applyCalibrationAndProceed(); return; }
    const nextPath = pointsToRequest.map((point, i) => [Math.round(point[0] * 1e6), Math.round(point[1] * 1e6), Math.round(point[2] * 1e6)]);
    let commands = [
      ['laserON'],
      ['sendCoordinates', nextPath],
      ['scanCoordinates'],
      ['laserOFF'],
      ['executeCommands']
    ];
    numSpectrumReqSentRef.current = nextPath.length;
    writeToDevice(characteristic, commands);

  }

  useEffect(() => {
    if (username) {
      const fetchUserGroups = async () => {
        const userGroups = await getUserGroups(username);
        setUserGroups(userGroups.map(group => group.group_name));
      };
      fetchUserGroups();
    }
  }, [username]);

  useEffect(() => {
    const initialLoad = async () => {
      let user = await Auth.currentAuthenticatedUser();
      let userTraceDetectionPreferences = { ...defaultTraceDetectionPreferences };
      try {
        const fetchedTraceDetectionPreferences = await getUserTraceDetectionPreferences(user.username);
        if (fetchedTraceDetectionPreferences?.success === false) {
          throw new Error(fetchedTraceDetectionPreferences.message);
        }
        userTraceDetectionPreferences = mergeTraceDetectionPreferences(fetchedTraceDetectionPreferences);
        await updateUserTraceDetectionPreferences(user.username, userTraceDetectionPreferences);
      } catch (error) {
        console.error('Failed to load user trace detection preferences. Using defaults.', error);
      }
      setUserTraceDetectionPreferences({ ...userTraceDetectionPreferences });
      setCurrentTraceDetectionPreferences({ ...userTraceDetectionPreferences });
      Sentry.setUser({ username: user.username });
      setCurrentUser(user);
      worker = new Worker(new URL('./worker.js', import.meta.url));
      worker.onmessage = e => workerMessageHandler(e);
      await loadModels();
      const loadedModel1 = await tf.loadGraphModel('/tf/ml/model_1/model.json');
      const loadedModel2 = await tf.loadGraphModel('/tf/ml/model_2/model.json');
      setModel_1(loadedModel1);
      setModel_2(loadedModel2);
      worker.postMessage({ type: 'INIT_MODELS' });
      for (let i = 0; i < numTensorflowWorkers; i++) {
        tensorflowWorker[i] = new Worker(new URL('./tensorflowWorker.js', import.meta.url));
        tensorflowWorker[i].onmessage = e => tensorflowWorkerMessageHandler(e);
        tensorflowWorker[i].postMessage({ type: 'INIT_MODELS' });
      }
    };
    initialLoad();
  }, []);

  useEffect(() => {
    if (primaryWorkerInitialized && tensorflowWorkersInitialized && clientUploadsWorkerInitialized) {
      setIsReady(true);
    }
  }, [tensorflowWorkersInitialized, clientUploadsWorkerInitialized, primaryWorkerInitialized]);

  useEffect(() => {
    if (bluetoothState === 'Disconnected') {
      setScanning(false);
    }
  }, [bluetoothState]);

  useEffect(() => { currentMapIDRef.current = currentMapID; }, [currentMapID]);
  useEffect(() => { traceMapRef.current = traceMap; }, [traceMap]);
  useEffect(() => { darkReferenceSpectrumRef.current = darkReferenceSpectrum; }, [darkReferenceSpectrum]);
  useEffect(() => { scanTypeRef.current = scanType; }, [scanType]);
  useEffect(() => { calibrateRef.current = calibrate; }, [calibrate]);
  useEffect(() => { scanningRef.current = scanning; }, [scanning]);
  useEffect(() => { scanPhaseRef.current = scanPhase; }, [scanPhase]);
  useEffect(() => { scanWidthRef.current = scanWidth; }, [scanWidth]);
  useEffect(() => { scanHeightRef.current = scanHeight; }, [scanHeight]);
  useEffect(() => { numLevelsRef.current = numLevels; }, [numLevels]);
  useEffect(() => { pointsPerSideRef.current = pointsPerSide; }, [pointsPerSide]);
  useEffect(() => { singlePointMultiplierRef.current = singlePointMultiplier; }, [singlePointMultiplier]);
  useEffect(() => { maxLaserPowerRef.current = maxLaserPower; }, [maxLaserPower]);
  useEffect(() => { maxIntegrationTimeRef.current = maxIntegrationTime; }, [maxIntegrationTime]);
  useEffect(() => { currentTraceDetectionPreferencesRef.current = currentTraceDetectionPreferences }, [currentTraceDetectionPreferences]);

  useEffect(() => {
    if (mapReprocessProgress > 0) {
      notificationAPI.open({
        key: 'reprocess',
        message: `Processing map...`,
        description: (<Progress percent={mapReprocessProgress} size="small" />),
        duration: 3,
        closeIcon: null
      });
    }
  }, [mapReprocessProgress]);

  function withTraceDetections(point) {
    const traceDetections = { ...(point.traceDetections || {}) };
    if (detectTraceHeroin(point)) {
      traceDetections.Heroin = true;
    } else {
      delete traceDetections.Heroin;
    }

    const nextPoint = { ...point };
    if (Object.keys(traceDetections).length) {
      nextPoint.traceDetections = traceDetections;
    } else {
      delete nextPoint.traceDetections;
    }
    return nextPoint;
  }

  useEffect(() => {
    if (mapReprocessTrigger) {

      const reprocessMap = async () => {
        setShouldReprocessTraceMap(true);
        let newMap = cloneDeep(map);
        for (const [i, dataPoint] of newMap.entries()) {
          setMapReprocessProgress(Math.round((i + 1) / newMap.length * 100));
          const halfMinRawSpectrumIntensity = Math.min(...dataPoint.rawSpectrum) / 2;
          dataPoint["darkSubtractedSpectrum"] = dataPoint.rawSpectrum.map((e, i) => e - darkReferenceSpectrumRef.current[i]);
          const minDarkSubtracted = Math.min(...dataPoint["darkSubtractedSpectrum"]);
          dataPoint["darkSubtractedSpectrum"] = dataPoint["darkSubtractedSpectrum"].map(e => e + (halfMinRawSpectrumIntensity - minDarkSubtracted));
          dataPoint["badPixelCorrectedSpectrum"] = removeBadPixels(dataPoint.darkSubtractedSpectrum, deviceInfoRef.current.bad_pixels);
          dataPoint["smoothedSpectrum"] = smooth(dataPoint.badPixelCorrectedSpectrum, null);
          const baseline = ASPLS({ data: dataPoint["smoothedSpectrum"], x_data: deviceWavenumbersRef.current });
          dataPoint["baselineCorrectedSpectrum"] = dataPoint["smoothedSpectrum"].map((e, i) => Math.max(0, e - baseline[i]));
          dataPoint["intensityCorrectedSpectrum"] = calibrateRamanIntensity(dataPoint.baselineCorrectedSpectrum, deviceInfoRef.current.intensity_calibration);
          dataPoint["interpolatedSpectrum"] = [...dataPoint["processedSpectrum"]];
          dataPoint["processedSpectrum"] = interpolate(standardWavenumbers, deviceWavenumbersRef.current, dataPoint["intensityCorrectedSpectrum"]);
          let q = Math.hypot(...dataPoint["interpolatedSpectrum"]);
          dataPoint["normalizedSpectrum"] = dataPoint["interpolatedSpectrum"].map((e, i) => e / q);
          dataPoint.result = (await getMLPredictionArray([dataPoint.processedSpectrum]))[0];
          dataPoint.reconstructionError = (await getReconstructionError([dataPoint.processedSpectrum]))[0];
          dataPoint.fentanylPrediction = (await getFentanylPrediction([dataPoint.processedSpectrum]))[0][0];
          dataPoint.xylazinePrediction = (await getXylazinePrediction([dataPoint.processedSpectrum]))[0][0];
          dataPoint.benzodiazepinePrediction = (await getBenzodiazepinePrediction([dataPoint.processedSpectrum]))[0][0];
          dataPoint.hydromorphonePrediction = (await getHydromorphonePrediction([dataPoint.processedSpectrum]))[0][0];
          dataPoint.label = classifySpectrum(dataPoint.processedSpectrum, dataPoint.result, dataPoint.reconstructionError, dataPoint.rawSpectrum, dataPoint.benzodiazepinePrediction);
          dataPoint.mlOutput = dataPoint.result[0].map((f, i) => [substanceArray[i], f, dataPoint.result[1][i], dataPoint.result[2][i]]).sort((a, b) => b[1] - a[1]).map(g => [g[0], Math.round(g[1] * 1000) / 1000, Math.round(g[2] * 1000) / 1000, Math.round(g[3] * 1000) / 1000]);
          newMap[i] = withTraceDetections(dataPoint);
        }
        setMap(newMap);
      }
      reprocessMap();
      setMapReprocessTrigger(false);
    }
  }, [mapReprocessTrigger]);

  useEffect(() => {
    setIsAdmin(currentUser?.attributes['custom:Role'] === '1');
    fetchSubstances().then((substancesFromDB) => {
      const allSubstancesData = {};
      substancesFromDB.forEach(s => {
        allSubstancesData[s.label] = { info: s.info };
      });
      setAllSubstancesData(allSubstancesData);
    });
  }, [currentUser]);

  useEffect(() => {
    pointsPerSideRef.current = pointsPerSide;
  }, [pointsPerSide, numLevels, singlePointMultiplier, scanType]);

  const ejectSample = async () => {
    if (bluetoothState === 'Connected') {
      await writeToDevice(characteristic, [
        ['stopScan'],
        ['laserOFF'],
        ['clearCoordinates'],
        ['move', [12.5e6, 20e6, 15e6]],
        ['move', [12.5e6, 50e6, 15e6]],
        ['executeCommands']
      ]);
    }
  }

  useEffect(() => {
    scanning ? console.time('Scan duration') : console.timeEnd('Scan duration');
    scanningRef.current = scanning;
    if (!scanning && scanPhase == 'Scanning') {
      setScanPhase('Finished');
      ejectSample();
      saveAllScanData();
      setRiskAwarenessShouldUpdate(true);
    } else if (!scanning && map.length === 0) {
      ejectSample();
      clear();
      setScanWidth(5);
      setPointsPerSide(scanSettings[scanType]);
    }
  }, [scanning]);

  useEffect(() => {
    if (isNewRecordCreated) {
      scanButtonHandler();
      setIsNewRecordCreated(false);
    }
  }, [isNewRecordCreated]);

  useEffect(() => {
    mapRef.current = map;
    updateTraceResults(map.filter(e => e.label !== 'Calibration'));
  }, [map]);

  useEffect(() => {
    deviceInfoRef.current = deviceInfo;
    if (deviceInfo) {
      setDeviceWavenumbers(
        getWavenumbers(500, deviceInfo.wavelength_calibration, deviceInfo.excitation_wavelength)
      );
    };
  }, [deviceInfo]);

  useEffect(() => {
    deviceWavenumbersRef.current = deviceWavenumbers;
  }, [deviceWavenumbers]);

  const mapViewButtonHandler = async (e, fromArchive = false) => {
    setLoadingMap(true);
    clear();
    let mapFromDB = fromArchive ? await fetchArchivedSpectra(e.map_id) : await fetchSpectrum(e.map_id);
    if (!mapFromDB?.length > 0) {
      message.error({ content: 'No spectra found in database.', key, duration: 10 });
      setTraceOptionsReady(true);
      setLoadingMap(false);
      return;
    }
    setCurrentMapID(e.map_id);
    let storedDeviceInfo = await fetchDeviceInfo(e.device_id);
    setDeviceInfo(storedDeviceInfo);
    setCurrentSerial(e.serial);
    let uniqueX = new Set(mapFromDB.map(e => e.coordinate[0]));
    let uniqueY = new Set(mapFromDB.map(e => e.coordinate[1]));
    let uniqueZ = new Set(mapFromDB.map(e => e.coordinate[2]));
    setCenter({
      x: (Math.max(...uniqueX) + Math.min(...uniqueX)) / 2,
      y: (Math.max(...uniqueY) + Math.min(...uniqueY)) / 2,
      z: (Math.max(...uniqueZ) + Math.min(...uniqueZ)) / 2,
    });
    let numPointsX = [...uniqueX].length;
    let numPointsY = [...uniqueY].length;
    setPointsPerSide(Math.max(numPointsX, numPointsY));
    let newScanWidth;
    if (numPointsX >= numPointsY) {
      newScanWidth = Math.max(...uniqueX) - Math.min(...uniqueX);
    } else {
      newScanWidth = Math.max(...uniqueY) - Math.min(...uniqueY);
    }
    setScanWidth(newScanWidth)
    setScanHeight(Math.max(...uniqueZ) - Math.min(...uniqueZ));
    if (newScanWidth === 0) { setSinglePointMultiplier(mapFromDB.length) }
    mapFromDB = mapFromDB.map(e => {
      let q = Math.hypot(...e.processed_spectrum);
      return { ...e, normalizedSpectrum: e.processed_spectrum.map(e => e / q) }
    });
    mapFromDB = mapFromDB.map(e => {
      e['badPixelCorrectedSpectrum'] = removeBadPixels(e.raw_spectrum, storedDeviceInfo.bad_pixels);
      return e;
    });
    let chuckedMapDB = chunkArrayIntoN(mapFromDB, numTensorflowWorkers);
    for (let i = 0; i < numTensorflowWorkers; i++) {
      tensorflowWorker[i].postMessage({ type: 'PREDICT', data: chuckedMapDB[i] });
    }
    const darkReferenceSpectrum = (await fetchDarkReferenceSpectrum(e.map_id)) || new Array(500).fill(0);
    mapFromDB = mapFromDB.map(e => {
      e['darkSubtractedSpectrum'] = e.raw_spectrum.map((e, i) => e - (darkReferenceSpectrum[i] * (e.integration_time / 500)));
      e['badPixelCorrectedSpectrum'] = removeBadPixels(e.darkSubtractedSpectrum, storedDeviceInfo.bad_pixels);
      return e;
    });
    setDarkReferenceSpectrum(darkReferenceSpectrum);
  }

  const clear = async () => {
    return new Promise(async (resolve, reject) => {
      clearTimeout(warmupDelayTimeoutID.current);
      bluetoothStream = [];
      totalNumSpectraAcquiredRef.current = 0;
      targetNumSpectraRef.current = 0;
      numSpectrumReqSentRef.current = 0;
      numSpectraAcquiredRef.current = 0;
      darkReferenceSpectraRef.current = [];
      setScanProgress(0);
      setMap([]);
      setTraceMap([]);
      setDarkReferenceSpectrum(null);
      setCurrentSerial(null);
      setCurrentMapID(null);
      referenceScanPhaseRef.current = 'calibrateY';
      dataPointProcessingInProgressRef.current = false;
      dataPointProcessingQueueRef.current = [];
      consecutiveSaturatedCountRef.current = 0;
      if (connectedDeviceInfoRef?.current?.center_calibration) {
        setCenter(prevCenter => {
          let newCenter = { ...prevCenter };
          newCenter.x = connectedDeviceInfoRef.current.center_calibration.x;
          newCenter.y = connectedDeviceInfoRef.current.center_calibration.y;
          return newCenter
        });
      } else {
        setCenter({ x: defaultCenter.x, y: defaultCenter.y, z: defaultCenter.z });
      }
      setDeviceInfo(connectedDeviceInfoRef.current);
      setScanPhase(null);
      setCurrentTraceDetectionPreferences({ ...userTraceDetectionPreferences });
      resolve();
    });
  }

  const postScanHandler = async dataPoint => {

    let skipProcessing = false;

    totalNumSpectraAcquiredRef.current += 1;
    numSpectraAcquiredRef.current += 1;
    setScanProgress(Math.floor((totalNumSpectraAcquiredRef.current / targetNumSpectraRef.current) * 100));

    dataPoint.iterationRef = totalNumSpectraAcquiredRef.current;

    const key = `${dataPoint.x},${dataPoint.y},${dataPoint.z}`;
    const keyData = pathHashMapRef.current.get(key);

    const existingDataPoint = mapRef.current.find(p => p.index === keyData.indices[keyData.counter]);

    const sensorSaturated = dataPoint.rawSpectrum.some(e => e >= 65535);

    if (existingDataPoint) {
      if (existingDataPoint.integrationTime === keyData.integrationTimes[keyData.counter]) {
        keyData.counter += 1;
        dataPoint.index = keyData.indices[keyData.counter];
        if (!dataPoint.index) {
          scanningRef.current = false;
          setScanning(false);
          console.error('Out-of-bounds counter. Data point index not found in path hash map.');
          console.error({ dataPoint, keyData, });
          return;
        }
        if (
          scanTypeRef.current === 'reference'
          && (referenceScanPhaseRef.current === 'LowPowerStackedIntegrationTimeSweep' || referenceScanPhaseRef.current === 'StackedIntegrationTimeSweep')
          && numSpectraAcquiredRef.current > 1
        ) {
          dataPoint.rawSpectrum = existingDataPoint.rawSpectrum.map((e, i) => e + dataPoint.rawSpectrum[i]);
          dataPoint.integrationTime = existingDataPoint.integrationTime + dataPoint.integrationTime;
        }
      } else {
        if (sensorSaturated) {
          keyData.integrationTimes[keyData.counter] = existingDataPoint.integrationTime;
          updateTargetNumSpectra();
          skipProcessing = true;
        } else {
          dataPoint.index = existingDataPoint.index;
          const updatedRawSpectrum = existingDataPoint.rawSpectrum.map((e, i) => e + dataPoint.rawSpectrum[i]);
          dataPoint.rawSpectrum = updatedRawSpectrum;
          dataPoint.integrationTime = existingDataPoint.integrationTime + dataPoint.integrationTime;
        }
      }
    } else {
      dataPoint.index = keyData.indices[keyData.counter];
      if (sensorSaturated) {
        keyData.integrationTimes[keyData.counter] = dataPoint.integrationTime;
        updateTargetNumSpectra();
      }
    }

    if (skipProcessing) {
      dataPointProcessingInProgressRef.current = false;
      if (numSpectraAcquiredRef.current === numSpectrumReqSentRef.current) {
        if (scanPhaseRef.current === 'Scanning') { processEpoch(); }
        if (scanPhaseRef.current === 'Calibrating') { processCalibrationEpoch(); }
      }
      return;
    }

    dataPoint.PathHashMapCounter = keyData.counter;

    const halfMinRawSpectrumIntensity = Math.min(...dataPoint.rawSpectrum) / 2;
    if (
      scanTypeRef.current === 'reference' &&
      (referenceScanPhaseRef.current === 'LowPowerIntegrationTimeSweep' || referenceScanPhaseRef.current === 'IntegrationTimeSweep')
    ) {
      const darkRefSpectra = mapRef.current.slice(0, 18).filter(p => p.integrationTime === dataPoint.integrationTime).map(p => p.rawSpectrum);
      const medianDarkReferenceSpectrum = darkRefSpectra[0].map((_, i) =>
        darkRefSpectra.map(s => s[i]).sort((a, b) => a - b)[Math.floor(darkRefSpectra.length / 2)]
      );
      dataPoint["darkSubtractedSpectrum"] = dataPoint.rawSpectrum.map((e, i) => e - medianDarkReferenceSpectrum[i]);
    } else {
      const numDarkSpectraToUse = dataPoint.integrationTime / 500;
      let darkReference = darkReferenceSpectraRef.current[0];
      for (let i = 1; i < numDarkSpectraToUse; i++) {
        const nextDarkReference = darkReferenceSpectraRef.current[i % darkReferenceSpectraRef.current.length];
        darkReference = darkReference.map((e, j) => e + nextDarkReference[j]);
      }
      dataPoint["darkSubtractedSpectrum"] = dataPoint.rawSpectrum.map((e, i) => e - (darkReferenceSpectrumRef.current[i] * (dataPoint.integrationTime / 500)));
    }
    const minDarkSubtracted = Math.min(...dataPoint["darkSubtractedSpectrum"]);
    dataPoint["darkSubtractedSpectrum"] = dataPoint["darkSubtractedSpectrum"].map(e => e + (halfMinRawSpectrumIntensity - minDarkSubtracted));
    dataPoint["badPixelCorrectedSpectrum"] = removeBadPixels(dataPoint.darkSubtractedSpectrum, deviceInfoRef.current.bad_pixels);
    dataPoint["smoothedSpectrum"] = smooth(dataPoint.badPixelCorrectedSpectrum, null);
    worker.postMessage({
      type: 'PROCESS',
      data: {
        dataPoint,
        deviceWavenumbers: deviceWavenumbersRef.current,
        deviceInfo: deviceInfoRef.current
      }
    });
  }

  function dequeueWorkerMessage() {
    if (workerMessageQueueRef.current.length > 0) {
      const dequeuedMessage = workerMessageQueueRef.current[0];
      workerMessageQueueRef.current = workerMessageQueueRef.current.slice(1);
      return dequeuedMessage;
    }
    return null;
  }

  const addDataPointToMap = (dataPoint) => {
    const nextDataPoint = withTraceDetections(dataPoint);
    const existingIndex = mapRef.current.findIndex(p => p.index === dataPoint.index);
    if (existingIndex !== -1) {
      mapRef.current[existingIndex] = nextDataPoint;
    } else {
      mapRef.current.push(nextDataPoint);
    }
    setMap([...mapRef.current]);
  }

  async function processWorkerMessages() {
    if (workerMessageProcessingRef.current) return;
    workerMessageProcessingRef.current = true;

    while (workerMessageQueueRef.current.length > 0) {
      const dataPoint = dequeueWorkerMessage();
      dataPoint.label = classifySpectrum(dataPoint.processedSpectrum, dataPoint.result, dataPoint.reconstructionError, dataPoint.rawSpectrum, dataPoint.benzodiazepinePrediction);
      dataPoint.mlOutput = dataPoint.result[0].map((f, i) => [substanceArray[i], f, dataPoint.result[1][i], dataPoint.result[2][i]]).sort((a, b) => b[1] - a[1]).map(g => [g[0], Math.round(g[1] * 1000) / 1000, Math.round(g[2] * 1000) / 1000, Math.round(g[3] * 1000) / 1000])
      if (scanningRef.current && scanTypeRef.current === 'reference') {

        addDataPointToMap(dataPoint);

        if (numSpectraAcquiredRef.current === numSpectrumReqSentRef.current) {
          if (referenceScanPhaseRef.current === 'IntegrationTimeSweep') { workerMessageProcessingRef.current = false; setScanning(false); return; }
          const { commands, nextPoints, integrationTimes, nextReferenceScanPhase } = processReferenceScanDataPoint(mapRef.current, dataPoint, referenceScanPhaseRef.current);
          referenceScanPhaseRef.current = nextReferenceScanPhase;
          if (Array.isArray(commands) && commands.length > 0) {
            updatePathHashMap(nextPoints, integrationTimes);
            const numSpectraRequested = commands.filter(e => e[0] === 'sendCoordinates').reduce((acc, curr) => acc + curr[1].flat().length / 3, 0);
            numSpectraAcquiredRef.current = 0;
            numSpectrumReqSentRef.current = numSpectraRequested;
            await writeToDevice(characteristic, commands);
          }
        }

      }
      if (scanningRef.current && scanTypeRef.current !== 'reference') {

        if (scanningRef.current && scanPhaseRef.current === 'Scanning') {

          // if (dataPoint.integrationTime >= maxIntegrationTimeRef.current / 5) {
          //   if (Math.max(...dataPoint.processedSpectrum) < 150) {
          //     const key = `${dataPoint.x},${dataPoint.y},${dataPoint.z}`;
          //     const keyData = pathHashMapRef.current.get(key);
          //     keyData.integrationTimes[dataPoint.PathHashMapCounter] = dataPoint.integrationTime;
          //     updateTargetNumSpectra();
          //   }
          // }

          addDataPointToMap(dataPoint);

          if (numSpectraAcquiredRef.current === numSpectrumReqSentRef.current) {
            processEpoch();
          }

        } else if (scanningRef.current && scanPhaseRef.current === 'Calibrating') {

          dataPoint.label = 'Calibration';
          addDataPointToMap(dataPoint);

          if (numSpectraAcquiredRef.current === numSpectrumReqSentRef.current) {
            processCalibrationEpoch();
          }

        }

      }
    }
    workerMessageProcessingRef.current = false;
    dataPointProcessingInProgressRef.current = false;
    if (scanningRef.current) { processDataPointQueue(); }
  }

  const processtensorflowWorkerMessages = async () => {
    if (tensorflowWorkerMessageQueue.current.length === numTensorflowWorkers) {
      let sortedFragments = tensorflowWorkerMessageQueue.current.filter(arr => arr.length > 0).sort((a, b) => a[0].index - b[0].index);
      let mapFromDB = sortedFragments.flat();
      let scanIsCalibrated = false;
      mapFromDB = mapFromDB.map(e => {
        const label = e.label === 'Calibration' ? e.label : classifySpectrum(e.processed_spectrum, e.result, e.reconstructionError, e.raw_spectrum, e.benzodiazepinePrediction)
        if (label === 'Calibration') { scanIsCalibrated = true; }
        if (e.result[0][substanceMap.get(label)] < substanceThresholds[label]) {
          e.result[0][substanceMap.get(label)] = substanceThresholds[label] + 0.001;
        }
        return { ...e, label }
      });
      setCalibrate(scanIsCalibrated);
      mapFromDB = mapFromDB.map(e => { return { ...e, mlOutput: e.result[0].map((f, i) => [substanceArray[i], f, e.result[1][i], e.result[2][i]]).sort((a, b) => b[1] - a[1]).map(g => [g[0], Math.round(g[1] * 1000) / 1000, Math.round(g[2] * 1000) / 1000, Math.round(g[3] * 1000) / 1000]) } });
      mapFromDB = mapFromDB.map((e) => {
        return withTraceDetections({
          index: e.index,
          x: e.coordinate[0],
          y: e.coordinate[1],
          z: e.coordinate[2],
          power: e.laser_power,
          integrationTime: e.integration_time,
          rawSpectrum: e.raw_spectrum,
          processedSpectrum: e.processed_spectrum,
          normalizedSpectrum: e.normalizedSpectrum,
          result: e.result,
          label: e.label,
          mlOutput: e.mlOutput,
          reconstructionError: e.reconstructionError,
          fentanylPrediction: e.fentanylPrediction,
          xylazinePrediction: e.xylazinePrediction,
          benzodiazepinePrediction: e.benzodiazepinePrediction,
          hydromorphonePrediction: e.hydromorphonePrediction,
          signalToNoise: e.signalToNoise,
          autocorrelationWidth: e.autocorrelationWidth,
          lowFrequencyEnergy: e.lowFrequencyEnergy
        })
      });
      setMap(mapFromDB);
      setScanPhase('Finished');
      tensorflowWorkerMessageQueue.current = [];
    }
  }

  const workerMessageHandler = e => {
    const { type, data } = e.data;
    switch (type) {
      case 'PROCESS_RESULT':
        workerMessageQueueRef.current.push(data);
        processWorkerMessages();
        break;
      case 'MODEL_INITIALIZED':
        console.log('Models in primary worker initialized!');
        setPrimaryWorkerInitialized(true);
    }
  }

  const tensorflowWorkerMessageHandler = e => {
    const { type, data } = e.data;
    switch (type) {
      case 'PREDICTION_RESULT':
        tensorflowWorkerMessageQueue.current.push(data);
        processtensorflowWorkerMessages();
        break;
      case 'MODEL_INITIALIZED':
        numTensorflowWorkersInitialized.current++;
        if (numTensorflowWorkersInitialized.current === numTensorflowWorkers) {
          setTensorflowWorkersInitialized(true);
        }
    }
  }

  const processDataPointQueue = () => {
    if (dataPointProcessingQueueRef.current.length > 0 && !dataPointProcessingInProgressRef.current) {
      dataPointProcessingInProgressRef.current = true;
      const dataPoint = dataPointProcessingQueueRef.current.shift();
      processDataPoint(dataPoint);
    }
  }

  const processDataPoint = async dataPoint => {
    if (darkReferenceSpectraRef.current.length < numDarkSpectraToAcquire) {
      darkReferenceSpectraRef.current.push(dataPoint.rawSpectrum);
      if (darkReferenceSpectraRef.current.length === numDarkSpectraToAcquire) {
        const masterDarkReferenceSpectrum = createMasterDarkReference(darkReferenceSpectraRef.current);
        setDarkReferenceSpectrum(masterDarkReferenceSpectrum);
      }
      dataPointProcessingInProgressRef.current = false;
      if (scanningRef.current) { processDataPointQueue(); }
    } else {
      postScanHandler(dataPoint);
    }
  }

  const handleCharacteristicValueChanged = async (event) => {
    if (!scanningRef.current) { return }
    let Uint16View = new Uint16Array(event.target.value.buffer);
    bluetoothStream.push(...Array.from(Uint16View));
    if (bluetoothStream.length >= 510) {
      let spectrumData = bluetoothStream.splice(0, 510);
      let rawSpectrum = spectrumData.slice(0, 500);
      let isSaturated = rawSpectrum.some(value => value >= 65535);
      if (isSaturated) {
        consecutiveSaturatedCountRef.current += 1;
        let numSaturatedPixels = rawSpectrum.filter(value => value >= 65535);
        if (consecutiveSaturatedCountRef.current === 1) {
          message.warning({ content: `Sensor saturation detected. ${numSaturatedPixels.length} pixel(s) affected.`, duration: 9 });
        } else if (consecutiveSaturatedCountRef.current === 2) {
          message.warning({ content: `Two consecutive sensor saturation events detected.`, duration: 9 });
        } else if (consecutiveSaturatedCountRef.current === 3) {
          setScanning(false);
          message.error({ content: `Three consecutive sensor saturation events detected. Aborting scan.`, duration: 9 });
        }
      } else if (consecutiveSaturatedCountRef.current > 0 && !isSaturated) {
        consecutiveSaturatedCountRef.current = 0;
      }
      let metaData16 = spectrumData.slice(500, 510);
      let metaData16View = new Uint16Array(metaData16);
      let metaData32View = new Uint32Array(metaData16View.buffer);
      let metaData = Array.from(metaData32View);
      let dataPoint = {};
      dataPoint.x = metaData[0] / 1e6;
      dataPoint.y = metaData[1] / 1e6;
      dataPoint.z = metaData[2] / 1e6;
      dataPoint.power = metaData[3];
      dataPoint.integrationTime = metaData[4];
      dataPoint.rawSpectrum = rawSpectrum;
      if (dataPoint.power > 450 || dataPoint.x > 25 || dataPoint.y > 50 || dataPoint.z > 20) {
        setScanning(false);
        message.error({ content: `Bluetooth transmission error detected. Please refresh the page.`, duration: 3600 });
        throw new Error('Bluetooth transmission error detected.');
      } else {
        dataPointProcessingQueueRef.current.push(dataPoint);
        if (scanningRef.current) { processDataPointQueue(); }
      }
    }
  }

  useEffect(() => {
    if (traceMap.length && loadingMap && currentSerial) {
      let scanIndex = allScanData.findIndex(e => e.key == currentSerial);
      const currentResults = scanIndex !== -1 ? allScanData[scanIndex].results : null;
      const substancesInTraceMap = new Set(traceMap.filter(e => e.label).map(e => e.label));
      const calculatedTraceDetectionPreferences = {};
      Object.entries(userTraceDetectionPreferences).forEach(e => {
        const traceSubstanceLabels = e[0] === 'Benzodiazepine' ? benzodiazepineLabels : [e[0]];
        const detected = [...substancesInTraceMap].filter(substance => traceSubstanceLabels.includes(substance));
        calculatedTraceDetectionPreferences[e[0]] = calculateIncludeValue({ traceSubstance: e[0], currentResults, detected, userPreference: e[1] });
      });
      setCurrentTraceDetectionPreferences({ ...calculatedTraceDetectionPreferences });
      setLoadingMap(false);
      message.success({ content: 'Loaded!', key, duration: 3 });
    }
  }, [traceMap, loadingMap, currentSerial]);

  const updateTraceResults = (pointsToProcess) => {

    if (pointsToProcess.length === 0) {
      setTraceMap([]);
      return;
    }

    const shouldShowTraceHeroinForMap = () =>
      shouldShowTraceHeroin(mapRef.current);

    const buildTracePoints = (point, hasTraceHeroin) => {
      let z = point.z;
      const { label, processedSpectrum, result, mlOutput, fentanylPrediction, xylazinePrediction, benzodiazepinePrediction, hydromorphonePrediction } = point;

      let { hasTraceFentanyl, traceFentanylLabel } = detectTraceFentanyl(label, processedSpectrum, result, fentanylPrediction, mlOutput);
      let { hasTraceXylazine, traceXylazineLabel } = detectTraceXylazine(label, processedSpectrum, result, mlOutput, xylazinePrediction);
      let hasTraceHydromorphone = detectTraceHydromorphone(label, processedSpectrum, result, hydromorphonePrediction);
      let hasTraceMedetomidine = detectTraceMedetomidine(label, result);
      let { hasTraceBenzodiazepine, traceBenzodiazepineLabel } = detectTraceBenzodiazepine(label, processedSpectrum, result, benzodiazepinePrediction, mlOutput);
      const tracePoints = [];

      if (hasTraceFentanyl) { z += 0.1; tracePoints.push({ ...point, z: z, label: traceFentanylLabel }); }
      if (hasTraceHydromorphone) { z += 0.1; tracePoints.push({ ...point, z: z, label: 'Hydromorphone' }); }
      if (hasTraceHeroin) { z += 0.1; tracePoints.push({ ...point, z: z, label: 'Heroin' }); }
      if (hasTraceMedetomidine) { z += 0.1; tracePoints.push({ ...point, z: z, label: 'Medetomidine' }); }
      if (hasTraceBenzodiazepine) { z += 0.1; tracePoints.push({ ...point, z: z, label: traceBenzodiazepineLabel }); }
      if (hasTraceXylazine) { z += 0.1; tracePoints.push({ ...point, z: z, label: traceXylazineLabel }); }
      if (!hasTraceFentanyl && !hasTraceHydromorphone && !hasTraceHeroin && !hasTraceMedetomidine && !hasTraceBenzodiazepine && !hasTraceXylazine) { tracePoints.push({}); }

      return tracePoints;
    };

    const buildTraceMap = (points, showTraceHeroin) => points.flatMap(point =>
      buildTracePoints(point, showTraceHeroin && point.traceDetections?.Heroin)
    );

    const points = (pointsToProcess[0]?.iterationRef)
      ? [pointsToProcess.reduce((latest, current) => (current.iterationRef >= latest.iterationRef ? current : latest), pointsToProcess[0])]
      : pointsToProcess;

    if (points === pointsToProcess) {
      const showTraceHeroin = shouldShowTraceHeroinForMap();
      setTraceMap(buildTraceMap(pointsToProcess, showTraceHeroin));
      return;
    }

    const previouslyShowingTraceHeroin = traceMapRef.current.some(point => point.label === 'Heroin');
    const showTraceHeroin = shouldShowTraceHeroinForMap();

    if (previouslyShowingTraceHeroin !== showTraceHeroin) {
      const pointsForRebuild = mapRef.current.filter(e => e.label !== 'Calibration');
      setTraceMap(buildTraceMap(pointsForRebuild, showTraceHeroin));
      return;
    }

    setTraceMap(draft => {
      for (let i = 0; i < points.length; i++) {
        let originalIndex = -1;
        for (let j = draft.length - 1; j >= 0; j--) {
          if (draft[j].index === points[i].index) {
            draft.splice(j, 1);
            originalIndex = j;
          }
        }
        const newTracePoints = buildTracePoints(points[i], showTraceHeroin && points[i].traceDetections?.Heroin);

        if (originalIndex !== -1) {
          draft.splice(originalIndex, 0, ...newTracePoints);
        } else {
          draft.push(...newTracePoints);
        }
      }
    });

  }

  const connectButtonCallback = async () => {
    try {
      if (bluetoothState === 'Connected') {
        await disconnectBluetoothDevice(device);
        setBluetoothState('Disconnected');
      } else {
        const [connectedDevice, bluetoothCharacteristic, wifiCharacteristic, bluetoothService] = await connectButtonHandler(onBluetoothDisconnect, message, key);
        device = connectedDevice;
        characteristic = bluetoothCharacteristic;
        setWifiCharacteristic(wifiCharacteristic);
        setBluetoothService(bluetoothService);
        let status = await subscribeToCharacteristic(characteristic, handleCharacteristicValueChanged);
        setBluetoothState(status);
        if (status === 'Connected') {
          message.success({ content: 'Connected to Series One', key, duration: 3 });
          let eepromData = await readEEPROM(characteristic);
          let eepromDataBuffer = arrayBufferToBufferCycle(eepromData.buffer);
          let deviceInfo = { ...parseEEPROM(eepromDataBuffer) }
          if (deviceInfo.device_firmware_version) {
            await updateDeviceFirmwareVersionInDB(deviceInfo.device_id, deviceInfo.device_firmware_version);
          }
          await updateDeviceNetworkStatusInDB(deviceInfo?.device_id, deviceInfo?.device_network_status);
          await updateDeviceCameraStatusInDB(deviceInfo?.device_id, deviceInfo?.device_camera_status);
          let storedDeviceInfo = await fetchDeviceInfo(deviceInfo.device_id);
          if (storedDeviceInfo.center_calibration) {
            deviceInfo.center_calibration = storedDeviceInfo.center_calibration;
            setCenter(prevCenter => {
              let newCenter = { ...prevCenter };
              newCenter.x = storedDeviceInfo.center_calibration.x;
              newCenter.y = storedDeviceInfo.center_calibration.y;
              return newCenter
            });
          }
          connectedDeviceInfoRef.current = deviceInfo;
          setDeviceInfo(deviceInfo);
          await writeToDevice(characteristic, [
            ['setStageAccelerationAndSpeedXY', [120, 18e6]],
            ['setStageAccelerationAndSpeedZ', [120, 42e6]],
            ['executeCommands']
          ]);
          return true
        } else {
          message.warning({ content: 'Failed to connect.', key, duration: 3 });
          return false;
        }
      }
    } catch (error) {
      message.warning({ content: 'Failed to connect.', key, duration: 3 });
    }
  }

  const onBluetoothDisconnect = () => {
    setBluetoothState('Disconnected');
    connectedDeviceInfoRef.current = null;
    setDeviceInfo(null);
  }
  const settingsToggleHandler = _ => { setSettings(!settings) };
  const deviceInfoToggleHandler = async () => {
    if (characteristic) {
      setShowDeviceInfo(!showDeviceInfo)
    } else {
      try {
        await connectButtonCallback();
        if (bluetoothState === 'Connected') {
          setShowDeviceInfo(!showDeviceInfo);
        } else {
          message.warning({ content: 'Failed to connect.', key, duration: 3 });
        }
      } catch (reason) {
        console.error(reason);
      }
    }
  };

  const scanButtonHandler = async () => {
    setScanPhase('Initializing');
    let path = currentPath.map((point, i) => [Math.round(point[0] * 1e6), Math.round(point[1] * 1e6), Math.round(point[2] * 1e6)]);
    updatePathHashMap(currentPath, Array(currentPath.length).fill(maxIntegrationTime), true);
    let initCommands = [];
    let scanDelay = 15000;
    if (deviceInfo.device_camera_status && (deviceInfo.device_network_status || imageCharacteristic)) {
      scanDelay += 20000;
      initCommands.push(
        ['move', [12.5e6, 45e6, 0e6]],
        ['captureImage', [imageFilename]],
      );
    }
    scanDelay += (600 * numDarkSpectraToAcquire); // extra time for dark reference scans

    initCommands.push(
      ['move', [12.5e6, 24e6, 15e6]],
      ['setIntegrationTime', 500],
      ['setLaserPower', 1],
      ['sendCoordinates', Array(numDarkSpectraToAcquire).fill([12.5e6, 24e6, 15e6])],
      ['scanCoordinates'] // standard dark reference
    );

    if (scanType === 'reference') {
      let path = [], integrationTimes = [];
      [500, 1000, 2000, 3000, 4000, 5000].forEach(integrationTime => {
        scanDelay += (integrationTime * 3);
        path.push(...Array(3).fill([12.5, 24, 15]));
        integrationTimes.push(...Array(3).fill(integrationTime));
        initCommands.push(
          ['setIntegrationTime', integrationTime],
          ['sendCoordinates', Array(3).fill([12.5e6, 24e6, 15e6])],
          ['scanCoordinates']
        );
      });
      updatePathHashMap(path, integrationTimes, true);
      updatePathHashMap(currentPath, Array(currentPath.length).fill(maxIntegrationTime), false);
    }

    initCommands.push(
      ['setLaserPower', 450], // laser warmup
      ['laserON'],
      ['executeCommands']
    );

    await writeToDevice(characteristic, initCommands);
    warmupDelayTimeoutID.current = setTimeout(async () => {
      let commands = [];
      if (scanType === 'reference') {
        setScanPhase('Scanning');
        commands.push(
          ['setIntegrationTime', 500],
          ['setLaserPower', 45],
          ['sendCoordinates', [...path, ...path]],
          ['scanCoordinates'],
          ['executeCommands']
        );
        const numSpectraRequested = commands.filter(e => e[0] === 'sendCoordinates').reduce((acc, curr) => acc + curr[1].flat().length / 3, 0);
        numSpectraAcquiredRef.current = 0;
        numSpectrumReqSentRef.current = numSpectraRequested;
        await writeToDevice(characteristic, commands);
      } else {
        if (!calibrateRef.current) {
          setScanPhase('Scanning');
          numSpectrumReqSentRef.current = path.length;
          commands.push(
            ['setIntegrationTime', 500],
            ['setLaserPower', maxLaserPowerRef.current],
            ['sendCoordinates', path],
            ['scanCoordinates']
          );
          commands.push(['laserOFF']);
          commands.push(['executeCommands']);
          await writeToDevice(characteristic, commands);
        } else {
          setScanPhase('Calibrating');
          const params = { center: { ...deviceInfoRef.current?.center_calibration, z: 14.25 }, scanHeight: 2, numLevels: 11 };
          let points = getExpandingSquareSearchPoints(params.center, 5, params.scanHeight, 1, params.numLevels, 1);
          let path = points.map((point, i) => [Math.round(point[0] * 1e6), Math.round(point[1] * 1e6), Math.round(point[2] * 1e6)]);
          updatePathHashMap(points, Array(points.length).fill(450 / maxLaserPowerRef.current * 500), true);
          numSpectrumReqSentRef.current = path.length;
          // Create path hash map for calibration scan (!)
          const commands = [['setLaserPower', maxLaserPowerRef.current], ['setIntegrationTime', 500], ['sendCoordinates', path], ['scanCoordinates'], ['executeCommands']];
          await writeToDevice(characteristic, commands);
        }
      }
    }, scanDelay);
  }

  const stepStartScan = _ => {
    clear();
    setScanning(true);
  }

  const saveSpectra = async ({ map, deviceID, mapID }) => {
    const res = await uploadSpectra({
      indices: map.map(e => e.index),
      coordinates: map.map(e => [e.x, e.y, e.z]),
      laserPowers: map.map(e => e.power),
      integrationTimes: map.map(e => e.integrationTime),
      rawSpectra: map.map(e => e.rawSpectrum),
      processedSpectra: map.map(e => e.processedSpectrum),
      labels: map.map(e => e.label),
      username,
      device_id: deviceID,
      map_id: mapID
    });
    return res.status === 'success';
  }

  const saveResults = async ({ mapID }) => {
    const results = computeResults(mapRef.current, traceMapRef.current, false, currentTraceDetectionPreferencesRef.current);
    const res = await uploadResults({ mapID, results });
    return res.status === 'success' || res.status === 'info';
  }

  const saveAllScanData = async _ => {
    message.loading({ content: 'Saving...', key, duration: 0 });
    const spectraSaved = await saveSpectra({ map: mapRef.current, deviceID: deviceInfoRef.current.device_id, mapID: currentMapIDRef.current });
    const darkReferenceSpectrumSaved = await saveDarkReferenceSpectrum({ map_id: currentMapIDRef.current, darkReferenceSpectrum: darkReferenceSpectrumRef.current });
    const resultsSaved = await saveResults({ mapID: currentMapIDRef.current });
    if (spectraSaved && resultsSaved && darkReferenceSpectrumSaved) {
      message.success({ content: 'Saved!', key, duration: 5 });
    } else {
      message.error({ content: 'Failed to save.', key, duration: 5 });
      console.error('Failed to save scan data:', {
        spectraSaved,
        darkReferenceSpectrumSaved,
        resultsSaved,
        mapID: currentMapIDRef.current,
        deviceID: deviceInfoRef.current.device_id,
      });
    }
  }

  const mainButtonHandler = async () => {
    if (scanPhase === 'Finished') {
      clear();
      setScanWidth(5);
      setPointsPerSide(scanSettings[scanType]);
      setCenter({ x: deviceInfo?.center_calibration.x || defaultCenter.x, y: deviceInfo?.center_calibration.y || defaultCenter.y, z: defaultCenter.z });
      setScanPhase(null);
      setIsResultOptionsPanelOpen(false);
    } else if (bluetoothState === 'Disconnected') {
      const connectedToDevice = await connectButtonCallback();
      if (connectedToDevice) {
        writeToDevice(characteristic, [
          ['laserOFF'],
          ['move', [12.5e6, 50e6, 20e6]],
          ['executeCommands']
        ]);
      }
    } else if (scanning) {
      setScanning(false);
    } else {
      setIsOperatorModalOpen(true);
    }
  }

  const handleScanPowerChange = e => {
    if (e.target.value === 'Full') {
      setMinLaserPower(450);
      setMaxLaserPower(450);
      setMinIntegrationTime(2500);
      setMaxIntegrationTime(2500);
      setLaserPowerSetting('Full');
      return;
    }
    if (e.target.value === 'Reduced') {
      setMinLaserPower(45);
      setMaxLaserPower(45);
      setMinIntegrationTime(20000);
      setMaxIntegrationTime(20000);
      setLaserPowerSetting('Reduced');
      return;
    }
  }

  useEffect(() => {
    let laserPowerSetting = null;
    if (minLaserPower === maxLaserPower && minIntegrationTime === maxIntegrationTime) {
      if (maxLaserPower === 450 && maxIntegrationTime === 2500) {
        laserPowerSetting = 'Full';
      } else if (maxLaserPower === 45 && maxIntegrationTime === 20000) {
        laserPowerSetting = 'Reduced';
      }
    }
    setLaserPowerSetting(laserPowerSetting);
  }, [minLaserPower, maxLaserPower, minIntegrationTime, maxIntegrationTime]);

  const getEstimatedScanTime = (scanType, lastKnownLaserPowerSetting) => {
    switch (scanType) {
      case 'quick':
        return lastKnownLaserPowerSetting === 'Full' ? 5 : 19;
      case 'standard':
        return lastKnownLaserPowerSetting === 'Full' ? 11 : 59;
      case 'detailed':
        return lastKnownLaserPowerSetting === 'Full' ? 18 : 111;
      case 'reference':
        return 5;
      default:
        return 0;
    }
  }

  const applyStandardScanSettings = (lastKnownLaserPowerSetting) => {
    setNumLevels(1);
    setScanWidth(5);
    setScanHeight(1);
    setSinglePointMultiplier(1);
    if (lastKnownLaserPowerSetting === 'Full') {
      setMinLaserPower(450);
      setMaxLaserPower(450);
      setMinIntegrationTime(2500);
      setMaxIntegrationTime(2500);
    } else if (lastKnownLaserPowerSetting === 'Reduced') {
      setMinLaserPower(45);
      setMaxLaserPower(45);
      setMinIntegrationTime(20000);
      setMaxIntegrationTime(20000);
    }
    setCalibrate(true);
  }

  return (
    <>
    {LOCAL_SCAN_STORAGE ?
      <Alert
        banner
        type="warning"
        message="You are using an offline version of the Scatr Dashboard. Scan data is stored only in this browser and will not be saved to Scatr's servers."
        className={classes.offlineBanner}
      /> : null
    }
    <Layout style={{ maxWidth: '1000px', margin: '0 auto', position: 'relative', background: 'white' }}>
      {
        !isReady ?
          <>
            <img src={logoImage} height={"36px"} alt="" style={{ position: 'absolute', top: '30px', left: '6%' }} />
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
              <div style={{ width: '150px', margin: '0 auto', textAlign: 'center' }}>
                <Lottie animationData={loadingAnimation} loop={true} />
                <div>Loading...</div>
              </div>
            </div>
          </> : null
      }
      <>
        <Content style={{
          opacity: isReady ? 1 : 0,
          visibility: isReady ? 'visible' : 'hidden',
          transition: 'opacity 1s ease, visibility 1s ease',
        }}>
          <img src={logoImage} height={"36px"} alt="" style={{ position: 'absolute', top: '30px', left: '6%' }} />
          {notificationContextHolder}
          {isInspectionModalOpen ? <InspectionModal map={map} /> : null}
          {isAllPredictionsPanelOpen ? <AllPredictionsPanel /> : null}
          {isValidatedSpectraTableOpen ? <ValidatedSpectraTable /> : null}
          {isResultOptionsPanelOpen ? <ResultOptionsPanel /> : null}
          <TenantManagerModal
            isModalOpen={isTenantsModalOpen}
            setIsTenantsModalOpen={setIsTenantsModalOpen}
          />
          <WifiConfigModal
            isModalOpen={wifiConfigModalOpen}
            setWifiConfigModalOpen={setWifiConfigModalOpen}
            bluetoothState={bluetoothState}
            characteristic={characteristic}
          />
          <OperatorModal stepStartScan={stepStartScan} />
          <Modal
            open={showDeviceInfo}
            title='Device Info'
            footer={[]}
            closable={true}
            onCancel={deviceInfoToggleHandler}
            width='80%'
          >
            <DeviceInfoTable />
          </Modal>
          <SubstanceInfoModal />
          <ActionButtons
            signOut={signOut}
            settingsToggleHandler={settingsToggleHandler}
            deviceInfoToggleHandler={deviceInfoToggleHandler}
            setWifiConfigModalOpen={setWifiConfigModalOpen}
            setIsTenantsModalOpen={setIsTenantsModalOpen}
            wifiConfigModalOpen={wifiConfigModalOpen}
            bluetoothState={bluetoothState}
            currentSerial={currentSerial}
          />
          <div style={{ width: '100%', height: '90px' }}></div>
          {
            settings ?
              <AdditionalSettings /> : null
          }
          <Row justify="center">
            <Col span={24} style={{ display: 'flex', justifyContent: 'center' }}>
              <Radio.Group value={scanType} disabled={scanning || map.length} >
                <Radio.Button value="quick" onChange={() => { setScanType('quick'); setPointsPerSide(scanSettings.quick); applyStandardScanSettings(lastKnownLaserPowerSetting); }} style={{ width: 120, height: 'auto', textAlign: 'center', lineHeight: 1, padding: '8px 0' }}>⚡️&nbsp;&nbsp;Quick<br /><span style={{ fontSize: '9px' }}>{getEstimatedScanTime('quick', lastKnownLaserPowerSetting)} min</span></Radio.Button>
                <Radio.Button value="standard" onChange={() => { setScanType('standard'); setPointsPerSide(scanSettings.standard); applyStandardScanSettings(lastKnownLaserPowerSetting); }} style={{ width: 120, height: 'auto', textAlign: 'center', lineHeight: 1, padding: '8px 0' }}>⚖️&nbsp;&nbsp;Standard<br /><span style={{ fontSize: '9px' }}>{getEstimatedScanTime('standard', lastKnownLaserPowerSetting)} min</span></Radio.Button>
                <Radio.Button value="detailed" onChange={() => { setScanType('detailed'); setPointsPerSide(scanSettings.detailed); applyStandardScanSettings(lastKnownLaserPowerSetting); }} style={{ width: 120, height: 'auto', textAlign: 'center', lineHeight: 1, padding: '8px 0' }}>🔬&nbsp;&nbsp;Detailed<br /><span style={{ fontSize: '9px' }}>{getEstimatedScanTime('detailed', lastKnownLaserPowerSetting)} min</span></Radio.Button>
                {(username && userGroups.length && userGroups.includes('reference_scanning')) &&
                  <Radio.Button value="reference" onChange={() => {
                    setScanType('reference');
                    setMinLaserPower(45);
                    setMaxLaserPower(45);
                    setMinIntegrationTime(1000);
                    setMaxIntegrationTime(1000);
                    setCenter({ x: deviceInfo?.center_calibration.x || defaultCenter.x, y: deviceInfo?.center_calibration.y || defaultCenter.y, z: defaultCenter.z });
                    setCalibrate(false);
                  }} style={{ width: 120, height: 'auto', textAlign: 'center', lineHeight: 1, padding: '8px 0' }}>💎&nbsp;&nbsp;Reference<br /><span style={{ fontSize: '9px' }}>5 min</span></Radio.Button>
                }
              </Radio.Group>
            </Col>
          </Row>
          <Row justify="center" style={{ gap: 24, marginBottom: 8 }}>
            <ScanWidthToggle />
            <LaserPowerToggle
              value={laserPowerSetting}
              onChange={handleScanPowerChange}
              disabled={scanning || map.length || scanType === 'reference'}
            />
          </Row>
          <div className={classes.mapContainer}>
            <PrimaryCharts currentSerial={currentSerial} currentMapID={currentMapID} />
          </div>
          <Row justify="center" align="middle" style={{ height: '100px', margin: '15px 0' }}>
            <Col span={24} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {
                (scanPhase === 'Initializing' || scanPhase === 'Calibrating' || scanPhase === 'Scanning') ?
                  <>
                    <p style={{ margin: 0 }}>{scanPhase}</p>
                    <div style={{ width: '100%', height: '47px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {scanning && (scanPhase === 'Initializing') ?
                        <Lottie animationData={lineSwirlLoadAnimation} loop={true} style={{ height: '50px', marginTop: '-15px' }} />
                        : null
                      }
                      {scanPhase === 'Scanning' || scanPhase === 'Calibrating' ? <Progress style={{ width: '50%' }} percent={scanProgress} status='active' /> : null}
                    </div>
                  </>
                  : null
              }
              <Button type={!scanning ? 'primary' : undefined} danger={scanning} ghost onClick={() => mainButtonHandler({ bluetoothState, scanning, scanPhase })}>
                {
                  scanPhase === 'Finished' ? 'Reset'
                    : bluetoothState === 'Disconnected' ? 'Connect'
                      : !scanning ? 'Start Scan' : 'Stop'
                }
              </Button>
            </Col>
          </Row>
          {settings ? <SettingsButtons /> : null}
          <ClientUploads
            mapViewButtonHandler={mapViewButtonHandler}
            setCurrentMapID={setCurrentMapID}
            map={map}
            traceMap={traceMap}
            setClientUploadsWorkerInitialized={setClientUploadsWorkerInitialized}
          />
          <Row justify="center" align="middle">
            <Col span={21}>
              <RiskAwareness />
            </Col>
          </Row>
          {(username && userGroups.length && userGroups.includes('laboratory_testing')) ?
            <Row justify="center" align="middle">
              <Col span={21}>
                <MassSpectrometryContainer />
              </Col>
            </Row> : null}
          {isAdmin && username === 'admin' ?
            <Row justify="center" align="middle">
              <Col span={21}>
                <SubstanceManagement />
              </Col>
            </Row> : null
          }
          <BluetoothImage />
        </Content >
        <Footer ref={footerRef} style={{ marginTop: '30px', textAlign: 'center', backgroundColor: 'white' }}>
          Copyright © {new Date().getFullYear()} Scatr Inc. All rights reserved. Oct 13, 2026 Version.<br />
          <a href="https://scatr.ca/documents/scatr-terms-of-use-2025-01-16.pdf" target="_blank">Terms of Use</a>
        </Footer>
      </>
    </Layout >
    </>
  );
}

export default App;
